/**
 * MaterialsDeliveryDatePicker · iter303ak-fix28 — Work Booked · T60 · Action 10
 * ───────────────────────────────────────────────────────────────────────────
 * Pick materials delivery date per (child × supplier). Defaults to
 * work_planned_start_date + default_offset_days (Trevor's spec: -2 days).
 *
 * Writes to captured_data:
 *   materials_delivery_dates: {
 *     [child_id]: { [supplier_slug]: iso_date }
 *   }
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Truck, AlertTriangle, Calendar } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Local slugify — mirror the backend's `routes.supplier_letterheads.slugify`.
// materials-overview returns `{ supplier: "Roofing Industries", line_count, materials }`
// without a slug — we compute one client-side for keying the delivery-date map.
const slugify = (s) => (s || '')
  .toString()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'unknown';

function addBusinessDays(isoDate, offsetDays) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '';
  // Simple calendar-day offset (matches Trevor's -2 default; business-day
  // math can layer on later if needed).
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function MaterialsDeliveryDatePicker({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const startDateSource = params.start_date_source || 'work_planned_start_date';
  const offsetDays = Number.isFinite(params.default_offset_days) ? params.default_offset_days : -2;
  const startDate = capturedData?.[startDateSource] || '';
  const defaultDeliveryDate = addBusinessDays(startDate, offsetDays);

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.appraisal_record?.child_decisions
      || capturedData?.appraisal?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  // Load supplier list per child from materials-overview.
  const [rows, setRows] = useState([]); // { child_id, appraisal_no, quote_type_ref, supplier_slug, supplier_name }
  const [dates, setDates] = useState(() => value?.materials_delivery_dates || value || {});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const list = [];
    const nextDates = { ...dates };
    for (const c of acceptedChildren) {
      try {
        const { data } = await api.get(`/appraisals/${c.child_id}/materials-overview`);
        const suppliers = Array.isArray(data?.suppliers) ? data.suppliers : [];
        for (const s of suppliers) {
          const slug = s.slug || slugify(s.supplier);
          const name = s.supplier || slug;
          if (!slug) continue;
          list.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no,
            quote_type_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            supplier_slug: slug,
            supplier_name: name,
          });
          nextDates[c.child_id] = nextDates[c.child_id] || {};
          if (!nextDates[c.child_id][slug]) {
            nextDates[c.child_id][slug] = defaultDeliveryDate;
          }
        }
      } catch { /* silent */ }
    }
    setRows(list);
    setDates(nextDates);
    setLoading(false);
  }, [acceptedChildren, defaultDeliveryDate]);   

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onChange) onChange({ materials_delivery_dates: dates });
  }, [dates]);   

  const setDate = (child_id, supplier_slug, v) => {
    setDates(prev => ({
      ...prev,
      [child_id]: { ...(prev[child_id] || {}), [supplier_slug]: v },
    }));
  };

  const persistAndContinue = async () => {
    try {
      if (parentAppraisalId) {
        await api.patch(`/appraisals/${parentAppraisalId}`, {
          materials_delivery_dates: dates,
        });
      }
    } catch { /* silent — captured_data has it */ }
    onNext();
  };

  const allPicked = rows.length > 0 && rows.every(r => !!(dates[r.child_id] || {})[r.supplier_slug]);

  return (
    <div className="space-y-4" data-testid="wf-materials-delivery-date">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
        <div className="flex items-start gap-3">
          <Truck className="w-5 h-5 mt-0.5 shrink-0 text-sky-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-sky-900">Set Materials Delivery Dates</div>
            <div className="text-sm mt-1 text-sky-800">
              {params.prompt || 'Pick a delivery date per supplier per quote. Defaults to 2 days before the planned start date.'}
            </div>
            {startDate && (
              <div className="text-xs mt-2 text-sky-700">
                Planned start: <b className="font-mono">{new Date(startDate).toLocaleDateString()}</b> · Default delivery: <b className="font-mono">{defaultDeliveryDate}</b>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading suppliers…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No suppliers found on accepted quotes yet. Set up materials in the child Materials tab first.
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map(r => {
            const key = `${r.child_id}|${r.supplier_slug}`;
            const val = (dates[r.child_id] || {})[r.supplier_slug] || '';
            const label = `${r.quote_type_ref || ''}${r.variant_label ? ` · ${r.variant_label}` : ''} — ${r.supplier_name}`;
            return (
              <div key={key} className="p-3 border border-slate-200 rounded-lg bg-white flex items-center gap-3 flex-wrap"
                data-testid={`wf-mdd-row-${r.child_id}-${r.supplier_slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{label}</span>
                    {r.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{r.appraisal_no}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-slate-500" />
                  <input type="date" value={val}
                    onChange={(e) => setDate(r.child_id, r.supplier_slug, e.target.value)}
                    data-testid={`wf-mdd-input-${r.child_id}-${r.supplier_slug}`}
                    className="px-2 py-1 text-xs border border-slate-300 rounded font-mono" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={!allPicked}
            data-testid="wf-mdd-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
